/*
  Step Labs quick shop panel (snippets/c-quick-shop.liquid).
  - open(trigger) / close(): shows the panel and returns focus to the trigger on close (Escape too).
  - Choosing a value updates the label, crosses out values with no available match and the price.
  - "Add to bag" adds the matching variant and opens the Rebuy Smart Cart drawer; without Rebuy,
    cart_type 'page' goes to the cart and anything else fires theme:cartchanged (as c-product-card).
*/
if (!customElements.get('c-quick-shop')) {
  customElements.define(
    'c-quick-shop',
    class CQuickShop extends HTMLElement {
      connectedCallback() {
        const data = this.querySelector('script[data-variants]');
        this.variants = data ? JSON.parse(data.textContent) : [];
        this.fieldsets = [...this.querySelectorAll('[data-position]')];
        this.addBtn = this.querySelector('[data-action="add"]');
        this.status = this.querySelector('.c-quick-shop__status');
        this.price = this.querySelector('[data-price]');

        // Start from the preselected (first available) variant.
        this.selected = {};
        this.fieldsets.forEach((fs) => {
          const on = fs.querySelector('[aria-pressed="true"]');
          if (on) this.selected[fs.dataset.position] = on.dataset.value;
        });

        this.querySelectorAll('.c-quick-shop__value').forEach((btn) => btn.addEventListener('click', () => this.choose(btn)));
        this.querySelector('[data-action="close"]').addEventListener('click', () => this.close());
        this.addBtn.addEventListener('click', () => this.add());
        this.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.stopPropagation(); this.close(); } });
        this.refresh();
      }

      open(trigger) {
        this.trigger = trigger;
        this.hidden = false;
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        const first = this.querySelector('.c-quick-shop__value[aria-pressed="true"], .c-quick-shop__add');
        if (first) first.focus({ preventScroll: true });
        this.dispatchEvent(new CustomEvent('quickshop:open', { bubbles: true }));
      }

      close() {
        if (this.hidden) return;
        this.hidden = true;
        if (this.status) this.status.textContent = '';
        if (this.trigger) {
          this.trigger.setAttribute('aria-expanded', 'false');
          this.trigger.focus({ preventScroll: true });
        }
      }

      choose(btn) {
        const fs = btn.closest('[data-position]');
        fs.querySelectorAll('.c-quick-shop__value').forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
        this.selected[fs.dataset.position] = btn.dataset.value;
        const label = fs.querySelector('[data-selected-label]');
        if (label) label.textContent = btn.dataset.value;
        this.refresh();
      }

      match() {
        return this.variants.find((v) => Object.entries(this.selected).every(([pos, val]) => v.options[pos - 1] === val)) || null;
      }

      refresh() {
        this.fieldsets.forEach((fs) => {
          const pos = Number(fs.dataset.position);
          const others = Object.entries(this.selected).filter(([p]) => Number(p) !== pos);
          fs.querySelectorAll('.c-quick-shop__value').forEach((btn) => {
            const possible = this.variants.some((v) => v.available && v.options[pos - 1] === btn.dataset.value && others.every(([p, val]) => v.options[p - 1] === val));
            btn.toggleAttribute('data-unavailable', !possible);
          });
        });
        const variant = this.match();
        if (variant && this.price) this.price.textContent = variant.price;
        this.addBtn.setAttribute('aria-disabled', variant && variant.available ? 'false' : 'true');
      }

      async add() {
        const variant = this.match();
        if (!variant || !variant.available || this.hasAttribute('data-busy')) return;
        this.setAttribute('data-busy', '');
        const root = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
        try {
          const res = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
          });
          const body = await res.json();
          if (!res.ok) throw new Error(body.description || body.message || 'Could not add to bag');
          if (this.status) this.status.textContent = 'Added to bag';
          if (await this.openRebuyCart()) {
            this.close();
            return;
          }
          if (this.dataset.cartType === 'page') {
            window.location.href = `${root}cart`;
            return;
          }
          document.documentElement.dispatchEvent(new CustomEvent('theme:cartchanged', { bubbles: true }));
          this.close();
        } catch (err) {
          if (this.status) this.status.textContent = err.message;
          console.error('[c-quick-shop]', err);
        } finally {
          this.removeAttribute('data-busy');
        }
      }

      // Rebuy Smart Cart is the store's cart drawer: refresh it and slide it open, no page change.
      // Returns false when Rebuy is not on the page, so the caller falls back to the theme cart.
      async openRebuyCart() {
        const rebuy = window.Rebuy;
        if (!rebuy || !rebuy.SmartCart || !rebuy.Cart) return false;
        try {
          await rebuy.Cart.fetchCart();
          rebuy.SmartCart.show();
          return true;
        } catch (err) {
          console.error('[rebuy]', err);
          return false;
        }
      }
    }
  );
}
